if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.sourceProductInfo = this.closest('product-info');
        this.sourceButton = document.getElementById(this.dataset.sourceButtonId);
        this.stickyButton = this.querySelector('[data-sticky-atc-submit]');
        this.stickyLabel = this.querySelector('[data-sticky-atc-label]');
        this.optionsSummary = this.querySelector('[data-sticky-atc-options]');
        this.stock = this.querySelector('.sbov3-sticky-atc__stock');
        this.stockText = this.querySelector('[data-sticky-atc-stock-text]');
        this.price = this.querySelector(`#StickyPrice-${this.dataset.sectionId}`);
        this.sourceButtonVisible = false;
        this.productSectionVisible = true;

        this.onStickyButtonClick = this.onStickyButtonClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);

        this.stickyButton?.addEventListener('click', this.onStickyButtonClick);
        this.observeSourceButton();
        this.observeProductSection();
        this.subscribeToVariantChanges();
        this.syncButtonState();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.stickyButton?.removeEventListener('click', this.onStickyButtonClick);
        this.sourceButtonObserver?.disconnect();
        this.sourceButtonMutationObserver?.disconnect();
        this.productSectionObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      observeSourceButton() {
        if (!this.sourceButton || typeof IntersectionObserver === 'undefined') return;

        this.sourceButtonObserver = new IntersectionObserver(([entry]) => {
          this.sourceButtonVisible = entry.isIntersecting && entry.intersectionRatio > 0;
          this.updateVisibility();
        });
        this.sourceButtonObserver.observe(this.sourceButton);

        this.sourceButtonMutationObserver = new MutationObserver(() => this.syncButtonState());
        this.sourceButtonMutationObserver.observe(this.sourceButton, {
          attributes: true,
          childList: true,
          subtree: true,
        });
      }

      observeProductSection() {
        if (!this.sourceProductInfo || typeof IntersectionObserver === 'undefined') return;

        this.productSectionObserver = new IntersectionObserver(([entry]) => {
          this.productSectionVisible = entry.isIntersecting;
          this.updateVisibility();
        });
        this.productSectionObserver.observe(this.sourceProductInfo);
      }

      subscribeToVariantChanges() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      onVariantChange(event) {
        if (String(event?.data?.sectionId) !== String(this.dataset.sectionId)) return;

        const sourcePrice = event.data.html?.getElementById(`price-${this.dataset.sectionId}`);
        if (sourcePrice && this.price) this.price.innerHTML = sourcePrice.innerHTML;

        this.updateVariantSummary(event.data.variant);
        this.updateStockStatus(event.data.variant);
        this.syncButtonState();
      }

      onStickyButtonClick() {
        if (!this.sourceButton || this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true') return;

        this.sourceButton.click();
      }

      syncButtonState() {
        if (!this.stickyButton) return;
        if (!this.sourceButton) {
          this.stickyButton.disabled = true;
          this.stickyButton.setAttribute('aria-disabled', 'true');
          return;
        }

        const sourceLabel = this.sourceButton.querySelector('span')?.textContent.trim();
        const isLoading = this.sourceButton.classList.contains('loading');
        const isDisabled = this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';

        if (sourceLabel && !isLoading) this.stickyLabel.textContent = sourceLabel;
        this.stickyButton.disabled = isDisabled;
        this.stickyButton.setAttribute('aria-disabled', String(isDisabled));
        this.stickyButton.classList.toggle('is-loading', isLoading);
        this.stickyButton.setAttribute('aria-busy', String(isLoading));
      }

      updateStockStatus(variant) {
        if (!this.stock || !this.stockText) return;

        let state = 'in-stock';
        let label = this.dataset.stockIn;
        const quantity = Number(variant?.inventory_quantity);
        const threshold = Number(this.dataset.stockThreshold) || 5;

        if (!variant) {
          state = 'unavailable';
          label = this.dataset.stockUnavailable;
        } else if (!variant.available) {
          state = 'out-of-stock';
          label = this.dataset.stockOut;
        } else if (variant.inventory_management === 'shopify' && Number.isFinite(quantity)) {
          if (quantity <= 0) {
            label = variant.inventory_policy === 'continue' ? this.dataset.stockContinue : this.dataset.stockOut;
            if (variant.inventory_policy !== 'continue') state = 'out-of-stock';
          } else if (quantity <= threshold) {
            state = 'low-stock';
            label = (this.dataset.stockLowCount || this.dataset.stockLow).replace('__quantity__', quantity);
          }
        }

        this.stock.dataset.state = state;
        this.stockText.textContent = label;
      }

      updateVariantSummary(variant) {
        if (!this.optionsSummary || !Array.isArray(variant?.options)) return;

        this.optionsSummary.textContent = variant.options.filter(Boolean).join(' · ');
      }

      updateVisibility() {
        const shouldShow = this.productSectionVisible && !this.sourceButtonVisible;
        this.dataset.visible = String(shouldShow);
        this.setAttribute('aria-hidden', String(!shouldShow));
      }
    }
  );
}
